/**
 * The Application Designer properties panel.
 *
 * Entirely generated from the cosmetic allowlist in
 * `lib/applicationDesigner/cosmetics.js`. There is no per-page and no
 * per-property markup here: a property's type decides its control, its group
 * decides where it appears, and a property the allowlist does not define cannot
 * be edited because no control exists for it. Opt-in box cosmetics (Card Width
 * and friends) stay hidden until the native element declares them, so they are
 * not added to every card automatically. Adding a safe cosmetic is a change to
 * the allowlist and its stylesheet rule, not to this panel.
 *
 * Every control offers "Default" as a first-class choice, which clears the
 * property rather than storing a value that happens to match the native look —
 * that is what keeps a saved layout sparse and lets the native design system
 * move underneath it.
 *
 * Grid geometry appears only for grid-mode pages, and only for elements whose
 * descriptor allows the corresponding capability. Flow pages show no geometry at
 * all, because a flow element's position is the column it was dragged into.
 */
import {
  BORDER_SIDES,
  COSMETIC_GROUPS,
  cosmeticPropertiesInGroup,
  cosmeticValue,
  withCosmetic,
} from "@/lib/applicationDesigner/cosmetics";
import { LAYOUT_MODE_GRID } from "@/lib/applicationDesigner/layoutModes";
import {
  descriptorCapabilities,
  descriptorElementTitle,
  elementAcceptsCosmetic,
} from "@/lib/applicationDesigner/pageDescriptor";
import { findLayoutItem } from "@/lib/applicationDesigner/layoutDocument";
import { designerTestIds } from "./designerTestIds";

function TokenControl({ property, value, testId, onChange }) {
  return (
    <select
      id={testId}
      data-testid={testId}
      value={value === null ? "" : value}
      onChange={(event) => onChange(event.target.value || null)}
    >
      <option value="">Default</option>
      {property.options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function ColorControl({ value, testId, clearTestId, onChange }) {
  return (
    <span className="ad-props__color">
      <input
        type="color"
        data-testid={testId}
        value={value || "#ffffff"}
        onChange={(event) => onChange(event.target.value)}
      />
      <button
        type="button"
        data-testid={clearTestId}
        className="ad-props__clear"
        disabled={value === null}
        title="Use the native colour"
        onClick={() => onChange(null)}
      >
        Default
      </button>
    </span>
  );
}

function SidesControl({ value, testId, onChange }) {
  const active = value || BORDER_SIDES;
  return (
    <span className="ad-props__sides" data-testid={testId}>
      {BORDER_SIDES.map((side) => (
        <label key={side}>
          <input
            type="checkbox"
            data-testid={`${testId}-${side}`}
            checked={active.includes(side)}
            onChange={(event) => {
              const next = event.target.checked
                ? [...active, side]
                : active.filter((item) => item !== side);
              onChange(next);
            }}
          />
          {side}
        </label>
      ))}
    </span>
  );
}

function PropertyRow({ property, style, ids, onChange }) {
  const value = cosmeticValue(style, property.key);
  const testId = `${ids.property}-${property.key}`;
  return (
    <div className="ad-props__field">
      <label htmlFor={testId}>{property.label}</label>
      {property.type === "color" ? (
        <ColorControl
          value={value}
          testId={testId}
          clearTestId={`${ids.propertyClear}-${property.key}`}
          onChange={(next) => onChange(property.key, next)}
        />
      ) : null}
      {property.type === "token" ? (
        <TokenControl
          property={property}
          value={value}
          testId={testId}
          onChange={(next) => onChange(property.key, next)}
        />
      ) : null}
      {property.type === "integer" ? (
        <input
          type="number"
          id={testId}
          data-testid={testId}
          min={property.min}
          max={property.max}
          value={value === null ? "" : value}
          placeholder="Default"
          onChange={(event) =>
            onChange(property.key, event.target.value === "" ? null : event.target.value)
          }
        />
      ) : null}
      {property.type === "boolean" ? (
        <input
          type="checkbox"
          id={testId}
          data-testid={testId}
          checked={value !== false}
          onChange={(event) => onChange(property.key, event.target.checked)}
        />
      ) : null}
      {property.type === "sides" ? (
        <SidesControl value={value} testId={testId} onChange={(next) => onChange(property.key, next)} />
      ) : null}
    </div>
  );
}

function GeometryRow({ label, testId, value, min, max, onChange }) {
  return (
    <div className="ad-props__field">
      <label htmlFor={testId}>{label}</label>
      <input
        type="number"
        id={testId}
        data-testid={testId}
        min={min}
        max={max}
        value={value}
        onChange={(event) => {
          const next = Number.parseInt(event.target.value, 10);
          if (Number.isFinite(next)) onChange(next);
        }}
      />
    </div>
  );
}

export default function DesignerProperties({ session, testIds }) {
  const ids = designerTestIds(testIds);
  if (!session.designing) return null;

  const item = findLayoutItem(session.items, session.selection);
  if (!item) {
    return (
      <aside className="ad-props" data-testid={ids.properties}>
        <h3 className="ad-props__title">Properties</h3>
        <p className="ad-props__note" data-testid={ids.propertiesEmpty}>
          Select an element to change how it looks. Its data, permissions and behaviour are owned by
          the page and are not editable here.
        </p>
      </aside>
    );
  }

  const capabilities = descriptorCapabilities(session.descriptor, item.id);
  const isGrid = session.descriptor.mode === LAYOUT_MODE_GRID;

  function change(key, value) {
    session.setStyle(item.id, withCosmetic(item.style, key, value));
  }

  return (
    <aside className="ad-props" data-testid={ids.properties}>
      <h3 className="ad-props__title">
        Properties
        <span className="ad-props__subject">{descriptorElementTitle(session.descriptor, item.id)}</span>
      </h3>

      {isGrid && (capabilities.move || capabilities.resize) ? (
        <section className="ad-props__group">
          <h4>Layout</h4>
          {capabilities.move ? (
            <>
              <GeometryRow
                label="Column"
                testId={`${ids.property}-col`}
                value={item.col}
                min={1}
                max={session.bounds.columns}
                onChange={(next) => session.moveTo(item.id, next, item.row)}
              />
              <GeometryRow
                label="Row"
                testId={`${ids.property}-row`}
                value={item.row}
                min={1}
                max={session.bounds.rows}
                onChange={(next) => session.moveTo(item.id, item.col, next)}
              />
            </>
          ) : null}
          {capabilities.resize ? (
            <>
              <GeometryRow
                label="Width (cols)"
                testId={`${ids.property}-width`}
                value={item.width}
                min={1}
                max={session.bounds.columns}
                onChange={(next) => session.resize(item.id, next, item.height)}
              />
              <GeometryRow
                label="Height (rows)"
                testId={`${ids.property}-height`}
                value={item.height}
                min={1}
                max={session.bounds.rows}
                onChange={(next) => session.resize(item.id, item.width, next)}
              />
            </>
          ) : null}
        </section>
      ) : null}

      {capabilities.style ? (
        COSMETIC_GROUPS.map((group) => {
          const properties = cosmeticPropertiesInGroup(group).filter((property) =>
            elementAcceptsCosmetic(session.descriptor, item.id, property.key),
          );
          if (!properties.length) return null;
          return (
            <section key={group} className="ad-props__group">
              <h4>{group}</h4>
              {properties.map((property) => (
                <PropertyRow
                  key={property.key}
                  property={property}
                  style={item.style}
                  ids={ids}
                  onChange={change}
                />
              ))}
            </section>
          );
        })
      ) : (
        <p className="ad-props__note">This element does not accept cosmetic changes.</p>
      )}
    </aside>
  );
}
